import React from 'react';
import { motion } from 'framer-motion';
import { Video, Sparkles, Palette, TrendingUp, Layers } from 'lucide-react';

const Services = () => {
  const fadeInUp = {
    initial: { opacity: 0, y: 60 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.6 }
  };

  const services = [
    {
      icon: <Video className="w-12 h-12" />,
      title: 'Media Production',
      description:
        'Professional video production, filming, editing, and multimedia creation. We bring your stories to life with cinematic quality and creative excellence.',
      image: 'https://images.unsplash.com/photo-1772459313426-4b75fc7c562e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjBmaWxtJTIwY2FtZXJhJTIwZXF1aXBtZW50JTIwc2V0fGVufDB8fHx8MTc3MzA0ODMzOHww&ixlib=rb-4.1.0&q=85',
    },
    {
      icon: <Sparkles className="w-12 h-12" />,
      title: 'Digital Content Creation',
      description:
        'High-quality content production for YouTube, social media, and online platforms. Engaging content that captures attention and drives results.',
      image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MjJ8MHwxfHNlYXJjaHwxfHxkaWdpdGFsJTIwbWFya2V0aW5nJTIwYW5hbHl0aWNzJTIwZGFzaGJvYXJkJTIwc2NyZWVufGVufDB8fHx8MTc3MzA0ODM0Mnww&ixlib=rb-4.1.0&q=85',
    },
    {
      icon: <Palette className="w-12 h-12" />,
      title: 'Branding & Advertising',
      description:
        'Developing brand identity, promotional content, and marketing campaigns that resonate with your target audience and build lasting connections.',
      image: 'https://images.unsplash.com/photo-1759552337652-95ee2815c9c8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MjJ8MHwxfHNlYXJjaHwzfHxkaWdpdGFsJTIwbWFya2V0aW5nJTIwYW5hbHl0aWNzJTIwZGFzaGJvYXJkJTIwc2NyZWVufGVufDB8fHx8MTc3MzA0ODM0Mnww&ixlib=rb-4.1.0&q=85',
    },
    {
      icon: <TrendingUp className="w-12 h-12" />,
      title: 'Marketing & Digital Strategy',
      description:
        'Helping businesses grow through data-driven digital marketing strategies. From SEO to social media, we cover all aspects of digital growth.',
      image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MjJ8MHwxfHNlYXJjaHwxfHxkaWdpdGFsJTIwbWFya2V0aW5nJTIwYW5hbHl0aWNzJTIwZGFzaGJvYXJkJTIwc2NyZWVufGVufDB8fHx8MTc3MzA0ODM0Mnww&ixlib=rb-4.1.0&q=85',
    },
    {
      icon: <Layers className="w-12 h-12" />,
      title: 'Custom Media Projects',
      description:
        'Tailored media and digital solutions depending on client requirements. We adapt to your unique needs and deliver customized solutions.',
      image: 'https://images.unsplash.com/photo-1772459313426-4b75fc7c562e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjBmaWxtJTIwY2FtZXJhJTIwZXF1aXBtZW50JTIwc2V0fGVufDB8fHx8MTc3MzA0ODMzOHww&ixlib=rb-4.1.0&q=85',
    },
  ];

  return (
    <div data-testid="services-page" className="min-h-screen pt-32">
      {/* Header */}
      <section className="py-16 px-6 md:px-12 lg:px-24">
        <div className="max-w-7xl mx-auto text-center">
          <motion.h1
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-5xl md:text-7xl font-outfit font-bold tracking-tighter text-white mb-6"
          >
            Our Services
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-base md:text-lg text-gray-400 font-manrope max-w-3xl mx-auto"
          >
            Comprehensive media production and digital services to elevate your brand and engage your audience.
          </motion.p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-16 px-6 md:px-12 lg:px-24">
        <div className="max-w-7xl mx-auto space-y-24">
          {services.map((service, index) => (
            <motion.div
              key={index}
              {...fadeInUp}
              data-testid={`service-detail-${index}`}
              className={`grid md:grid-cols-2 gap-12 items-center ${
                index % 2 === 1 ? 'md:flex-row-reverse' : ''
              }`}
            >
              <div className={index % 2 === 1 ? 'md:order-2' : ''}>
                <div className="text-[#7c3aed] mb-6">{service.icon}</div>
                <h2 className="text-3xl md:text-4xl font-outfit font-bold tracking-tighter text-white mb-4">
                  {service.title}
                </h2>
                <p className="text-base md:text-lg text-gray-400 font-manrope leading-relaxed">
                  {service.description}
                </p>
              </div>
              <div className={index % 2 === 1 ? 'md:order-1' : ''}>
                <img
                  src={service.image}
                  alt={service.title}
                  className="rounded-3xl border border-white/10 w-full h-auto hover:border-[#7c3aed]/50 transition-all duration-300"
                />
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 px-6 md:px-12 bg-gradient-to-br from-[#7c3aed] to-[#3b82f6]">
        <div className="max-w-4xl mx-auto text-center">
          <motion.h2
            {...fadeInUp}
            className="text-4xl md:text-5xl font-outfit font-bold tracking-tighter text-white mb-8"
          >
            Ready to Start Your Project?
          </motion.h2>
          <motion.p
            {...fadeInUp}
            className="text-lg text-white/90 font-manrope mb-8"
          >
            Let's discuss how we can help bring your vision to life.
          </motion.p>
          <motion.div {...fadeInUp}>
            <a
              href="/contact"
              data-testid="services-cta-btn"
              className="inline-block bg-white text-[#7c3aed] font-manrope font-bold px-10 py-5 rounded-full hover:scale-105 transition-transform duration-200 text-lg"
            >
              Get In Touch
            </a>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Services;